import React, { useState, useEffect } from 'react';
import { X, Cloud, LogIn, LogOut, Save, Download, Trash2, AlertCircle } from 'lucide-react';
import { auth, db, googleProvider, handleFirestoreError, OperationType } from '../firebase';
import { signInWithPopup, signOut, onAuthStateChanged, User } from 'firebase/auth';
import { collection, doc, setDoc, getDocs, deleteDoc, query, where, serverTimestamp } from 'firebase/firestore';
import { MindMapNode, ColorPalette } from '../types';
import { translations, Language } from '../utils/translations';
import { convertTreeToMarkdown } from '../utils/markdownParser';
import { useMindMapStore } from '../store/useMindMapStore';

interface CloudModalProps {
  isOpen: boolean;
  onClose: () => void;
  language: Language;
  currentData: MindMapNode | null;
  currentPalette: ColorPalette;
  fileName: string;
  currentMindmapId: string | null;
  sessionId: string;
  onSetCurrentMindmapId: (id: string) => void;
  onLoadMindmap: (markdown: string, title: string, mapId: string) => void;
  getViewState: () => { x: number; y: number; k: number } | undefined;

  onSaveSuccess?: (savedTitle: string) => void;
}

interface SavedMindmap {
  id: string;
  title: string;
  rawMarkdown: string;
  createdAt: any;
  updatedAt: any;
}

export const CloudModal: React.FC<CloudModalProps> = ({
  isOpen,
  onClose,
  language,
  currentData,
  currentPalette,
  fileName,
  onLoadMindmap,
  getViewState,
  onSaveSuccess,
  currentMindmapId,
  sessionId,
  onSetCurrentMindmapId
}) => {

  const [user, setUser] = useState<User | null>(null);
  const [activeTab, setActiveTab] = useState<'save' | 'load'>('save');
  const store = useMindMapStore();
  const getInitialTitle = () => {
    const clean = fileName.replace(/\.(md|txt)$/i, '');
    const isDefault = clean.toLowerCase() === 'untitled' || clean.toLowerCase() === 'mindmap demo' || !currentMindmapId;
    if (isDefault && currentData?.name?.trim()) {
      return currentData.name.trim();
    }
    return clean;
  };

  const [saveTitle, setSaveTitle] = useState(getInitialTitle);
  const [savedMaps, setSavedMaps] = useState<SavedMindmap[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [asyncError, setAsyncError] = useState<Error | null>(null);
  const t = translations[language];

  if (asyncError) throw asyncError;

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      if (currentUser && isOpen && activeTab === 'load') {
        fetchSavedMaps(currentUser.uid);
      }
    });
    return () => unsubscribe();
  }, [isOpen, activeTab]);

  useEffect(() => {
    if (isOpen) {
      setSaveTitle(getInitialTitle());
      setError(null);
      if (user && activeTab === 'load') {
        fetchSavedMaps(user.uid);
      }
    }
  }, [isOpen, fileName, user, activeTab, currentData, currentMindmapId]);

  const fetchSavedMaps = async (userId: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const q = query(collection(db, 'mindmaps'), where('userId', '==', userId));
      const querySnapshot = await getDocs(q);
      const maps: SavedMindmap[] = [];
      querySnapshot.forEach((doc) => {
        maps.push({ id: doc.id, ...doc.data() } as SavedMindmap);
      });
      // Sort by updatedAt descending
      maps.sort((a, b) => {
        const timeA = a.updatedAt?.toMillis ? a.updatedAt.toMillis() : 0;
        const timeB = b.updatedAt?.toMillis ? b.updatedAt.toMillis() : 0;
        return timeB - timeA;
      });
      setSavedMaps(maps);
    } catch (err) {
      try {
        handleFirestoreError(err, OperationType.LIST, 'mindmaps');
      } catch (e) {
        setAsyncError(e as Error);
      }
      setError('Failed to load mindmaps.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async () => {
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (err: any) {
      console.error('Login error:', err);
      if (err.code === 'auth/unauthorized-domain') {
        setError('Unauthorized domain. Please add localhost to Authorized Domains in Firebase Console.');
      } else {
        setError('Failed to log in.');
      }
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  const handleSave = async () => {
    if (!user || !currentData) return;
    setIsLoading(true);
    setError(null);
    try {
      const viewState = getViewState();
      const markdown = convertTreeToMarkdown(currentData, currentPalette.name, viewState, store.history);
      
      const docId = currentMindmapId || `map_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      const docRef = doc(db, 'mindmaps', docId);
      
      await setDoc(docRef, {
        userId: user.uid,
        title: saveTitle || 'Untitled Mindmap',
        rawMarkdown: markdown,
        updatedAt: serverTimestamp(),
        sessionId,
        ...(currentMindmapId ? {} : { createdAt: serverTimestamp() })
      }, { merge: true });
      
      const finalTitle = saveTitle.trim() || 'Untitled Mindmap';
      if (!currentMindmapId) onSetCurrentMindmapId(docId);
      if (onSaveSuccess) onSaveSuccess(finalTitle);
      onClose();

    } catch (err) {
      try {
        handleFirestoreError(err, OperationType.CREATE, 'mindmaps');
      } catch (e) {
        setAsyncError(e as Error);
      }
      setError('Failed to save mindmap.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoad = (map: SavedMindmap) => {
    onLoadMindmap(map.rawMarkdown, map.title + '.md', map.id);
    onClose();
  };


  const handleDelete = async (id: string) => {
    if (!user) return;
    setIsLoading(true);
    setError(null);
    try {
      await deleteDoc(doc(db, 'mindmaps', id));
      setSavedMaps(prev => prev.filter(m => m.id !== id));
    } catch (err) {
      try {
        handleFirestoreError(err, OperationType.DELETE, `mindmaps/${id}`);
      } catch (e) {
        setAsyncError(e as Error);
      }
      setError('Failed to delete mindmap.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700 w-full max-w-md p-6 transform transition-all scale-100 animate-in fade-in zoom-in duration-200 flex flex-col max-h-[80vh]">
        
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-2 text-slate-800 dark:text-slate-100">
            <Cloud className="w-6 h-6 text-indigo-500" />
            <h3 className="text-xl font-bold">Cloud Storage</h3>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200">
            <X className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg flex items-center gap-2 text-sm">
            <AlertCircle className="w-4 h-4" />
            {error}
          </div>
        )}

        {!user ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <Cloud className="w-16 h-16 text-slate-300 dark:text-slate-600 mb-4" />
            <p className="text-slate-600 dark:text-slate-300 mb-6">
              Sign in to save and load your mindmaps across devices.
            </p>
            <button
              onClick={handleLogin}
              className="flex items-center gap-2 px-6 py-3 bg-indigo-500 hover:bg-indigo-600 text-white font-medium rounded-lg shadow-lg shadow-indigo-500/30 transition-all"
            >
              <LogIn className="w-5 h-5" />
              Sign in with Google
            </button>
          </div>
        ) : (
          <div className="flex flex-col flex-1 overflow-hidden">
            <div className="flex items-center justify-between mb-4 pb-4 border-b border-slate-200 dark:border-slate-700">
              <div className="flex items-center gap-2">
                <img src={user.photoURL || ''} alt="User" className="w-8 h-8 rounded-full bg-slate-200" />
                <div className="text-sm">
                  <p className="font-medium text-slate-800 dark:text-slate-200">{user.displayName}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">{user.email}</p>
                </div>
              </div>
              <button onClick={handleLogout} className="p-2 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200" title="Sign out">
                <LogOut className="w-4 h-4" />
              </button>
            </div>

            <div className="flex gap-2 mb-4">
              <button
                onClick={() => setActiveTab('save')}
                className={`flex-1 py-2 text-sm font-medium rounded-lg transition-colors ${activeTab === 'save' ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300' : 'text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800'}`}
              >
                Save
              </button>
              <button
                onClick={() => setActiveTab('load')}
                className={`flex-1 py-2 text-sm font-medium rounded-lg transition-colors ${activeTab === 'load' ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300' : 'text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800'}`}
              >
                Load
              </button>
            </div>

            <div className="flex-1 overflow-y-auto min-h-[200px]">
              {activeTab === 'save' ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Mindmap Title</label>
                    <input
                      type="text"
                      value={saveTitle}
                      onChange={(e) => setSaveTitle(e.target.value)}
                      className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 outline-none"
                      placeholder="Enter a title..."
                    />
                  </div>
                  <button
                    onClick={handleSave}
                    disabled={isLoading || !saveTitle.trim()}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-indigo-500 hover:bg-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-lg shadow-lg shadow-indigo-500/30 transition-all"
                  >
                    {isLoading ? 'Saving...' : <><Save className="w-4 h-4" /> Save to Cloud</>}
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  {isLoading && savedMaps.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">Loading...</div>
                  ) : savedMaps.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">No saved mindmaps found.</div>
                  ) : (
                    savedMaps.map(map => (
                      <div key={map.id} className="flex items-center justify-between p-3 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                        <div className="flex-1 min-w-0 pr-4">
                          <h4 className="font-medium text-slate-800 dark:text-slate-200 truncate">{map.title}</h4>
                          <p className="text-xs text-slate-500 truncate">
                            {map.updatedAt?.toDate ? map.updatedAt.toDate().toLocaleString() : 'Just now'}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <button
                            onClick={() => handleLoad(map)}
                            className="p-2 text-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 rounded-lg transition-colors"
                            title="Load"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(map.id)}
                            className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
